/**
 * THE `planningRunDetail` SLOT (`SPEC-APP.md` §11.6). `run` is its only prop and supplies the id.
 *
 * THIS IS THE FROZEN VIEW — what this run actually used. §11.1's workspace banner is the LIVE view
 * of the current delivery per feed; the two must never read alike on screen, so this meta row says
 * so in words and carries the run's cutoff instead of a received time.
 *
 * A `404` is an EMPTY STATE, never a `ProblemAlert` — the rule group 6's `ReportFrame` owns for a
 * report with no data. Any other failure still renders `ProblemAlert`.
 *
 * The tab title is §11.6's own sentence, carried as a static `title` on this component, so the
 * strip does not read the module's name.
 */
import { Link } from "@tanstack/react-router";
import { FileClock } from "lucide-react";

import type { PlanningRun } from "../../../api/client";
import { EmptyState } from "../../../components/common/EmptyState";
import { ProblemAlert } from "../../../components/common/ProblemAlert";
import { Skeleton } from "../../../components/ui/skeleton";
import { formatDateTime } from "../../../domain/format";
import { RunSalesOutboundTable } from "../components/common/RunSalesOutboundTable";
import { useRunSalesOutbound } from "../hooks/useRunSalesOutbound";

/** §11.6's own words. Never core's, and never "snapshot" as user-facing copy. */
export const RUN_SALES_OUTBOUND_TITLE = "Sales outbound used by this run";

export function RunSalesOutboundTab({ run }: { run: PlanningRun }) {
  const { data, isLoading, problem, notRecorded, refetch } = useRunSalesOutbound(run.id);

  if (notRecorded) {
    return (
      <EmptyState
        icon={FileClock}
        variant="first-run"
        title="This run has no sales outbound recorded"
      />
    );
  }

  if (problem) return <ProblemAlert problem={problem} onRetry={refetch} />;

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-xs">
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="text-muted-foreground type-caption flex flex-wrap items-center gap-sm">
        <span className="border-border text-muted-foreground flex items-center gap-2xs rounded-sm border px-2xs py-[1px]">
          <FileClock aria-hidden className="size-3" />
          Frozen — what this run used
        </span>
        <span>Cutoff {formatDateTime(data.cutoffAt)}</span>
        {data.publishedAt ? <span>Published {formatDateTime(data.publishedAt)}</span> : null}
        <Link
          to="/outbound/snapshots/$snapshotId"
          params={{ snapshotId: data.snapshotId }}
          className="underline"
        >
          Open the outbound it was sent with
        </Link>
      </div>

      <RunSalesOutboundTable rows={data.rows} />
    </div>
  );
}

RunSalesOutboundTab.title = RUN_SALES_OUTBOUND_TITLE;

export default RunSalesOutboundTab;
